// components/theme-menu.tsx
"use client";

import React, { useMemo, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"; // if you scaffolded with shadcn
import { ChevronDown, Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";

export function ThemeMenu() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  // Prevent rendering mismatch (next-themes pattern)
  useEffect(() => setMounted(true), []);

  const currentIcon = useMemo(() => {
    if (theme === "light") {
      return <Sun className="w-4 h-4" />;
    } else if (theme === "dark") {
      return <Moon className="w-4 h-4" />;
    }
    //  else if (theme === "system") {
    return <Monitor className="w-4 h-4" />;
  }, [theme]);

  if (!mounted)
    return (
      // lightweight placeholder to avoid hydration mismatch
      <div className="w-10 h-10 rounded-md bg-muted/70 animate-pulse" />
    );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-2 rounded-md px-3 py-2 bg-white/6 backdrop-blur-sm hover:bg-white/8 border border-white/6"
          aria-label="Choose theme"
        >
          <Monitor className="w-4 h-4" />
          <span className="sr-only">Theme</span>
          <ChevronDown className="w-4 h-4 opacity-70" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[360px] p-4 bg-popover text-popover-foreground"
      >
        <div className="flex items-center justify-between mb-2">
          {/* <div className="flex items-center gap-2"> */}
          <div className="rounded-full bg-accent/10 p-1">{currentIcon}</div>
          <DropdownMenuLabel className="text-sm">
            Choose Theme
          </DropdownMenuLabel>
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuRadioGroup
          className="border"
          value={theme ?? "system"}
          onValueChange={(v) => setTheme(v)}
        >
          <DropdownMenuRadioItem
            value="light"
            className="flex flex-col justify-center gap-0.5 py-2"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-white/5">
                  <Sun className="w-4 h-4" />
                </div>
                <div className="text-sm">Light</div>
              </div>
              <div className="text-xs text-muted-foreground">Light theme</div>
            </div>
          </DropdownMenuRadioItem>

          <DropdownMenuRadioItem
            value="dark"
            className="flex flex-col justify-center gap-0.5 py-2"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-white/5">
                  <Moon className="w-4 h-4" />
                </div>
                <div className="text-sm">Dark</div>
              </div>
              <div className="text-xs text-muted-foreground">Dark theme</div>
            </div>
          </DropdownMenuRadioItem>

          <DropdownMenuRadioItem
            value="system"
            className="flex flex-col justify-center gap-0.5 py-2"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-white/5">
                  <Monitor className="w-4 h-4" />
                </div>
                <div className="text-sm">System</div>
              </div>
              {/* <div className="text-xs text-muted-foreground text-ellipsis">Follow system preference</div> */}
              <div className="text-xs text-muted-foreground text-ellipsis">
                System preference
              </div>
            </div>
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        <div className="mt-2 flex items-center justify-between px-1">
          <div className="text-xs text-muted-foreground">Current System</div>
          <div className="flex items-center gap-2 text-sm">
            <Moon className="w-4 h-4" />
            <span className="capitalize">{resolvedTheme}</span>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
